import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Button, Icon, message, Modal, Popover, Tag } from "antd";
import moment from "moment";

import { request } from "~/components/Instance";
import { supabase } from "~/lib/supabase";

// Public Meta App IDs (set at build time). Instagram Login uses a separate
// Instagram App ID, distinct from the Facebook App ID — we prefer the IG one
// and fall back to the FB one for older deploys.
var META_APP_ID = (typeof process !== 'undefined' && process.env &&
    (process.env.REACT_APP_META_IG_APP_ID || process.env.REACT_APP_META_APP_ID)) || '';
var SUPABASE_URL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SUPABASE_URL) || '';
var SUPABASE_ANON_KEY = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SUPABASE_ANON_KEY) || '';

var IG_SCOPES = [
    'instagram_business_basic',
    'instagram_business_manage_messages',
].join(',');

// Reuse the same token-cache pattern as NotificationContext so we don't fetch a JWT twice.
function getSupabaseToken() {
    return request('GET', '/supabase-token', null, false).then(function(res) {
        return res && res.data && res.data.token;
    });
}

var InstagramConnect = function() {
    var history = useHistory();
    var [loading, setLoading] = useState(true);
    var [connection, setConnection] = useState(null);
    var [connecting, setConnecting] = useState(false);

    var loadStatus = useCallback(function() {
        setLoading(true);
        getSupabaseToken()
            .then(function(token) {
                if (!token) throw new Error('no-token');
                supabase.realtime.setAuth(token);
                return fetch(SUPABASE_URL + '/rest/v1/instagram_connections?is_active=eq.true&select=page_id,username,subscribed_at,last_event_at', {
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'apikey': SUPABASE_ANON_KEY,
                    },
                }).then(function(r) { return r.ok ? r.json() : []; });
            })
            .then(function(rows) {
                setConnection(Array.isArray(rows) && rows.length ? rows[0] : null);
            })
            .catch(function() { setConnection(null); })
            .finally(function() { setLoading(false); });
    }, []);

    useEffect(function() {
        loadStatus();
    }, [loadStatus]);

    var handleConnect = useCallback(function() {
        if (!META_APP_ID) {
            Modal.error({
                title: 'Configuração pendente',
                content: 'A integração com Instagram ainda não foi configurada (META_APP_ID ausente). Contate o suporte.',
            });
            return;
        }
        var redirectUri = window.location.origin + '/oauth/instagram';
        var authUrl = 'https://www.instagram.com/oauth/authorize'
            + '?client_id=' + encodeURIComponent(META_APP_ID)
            + '&redirect_uri=' + encodeURIComponent(redirectUri)
            + '&scope=' + encodeURIComponent(IG_SCOPES)
            + '&response_type=code'
            + '&state=instagram_connect';

        var popup = window.open(authUrl, 'ig-oauth', 'width=640,height=720');
        setConnecting(true);

        var finish = function() {
            setConnecting(false);
            window.removeEventListener('message', messageHandler);
            clearInterval(poll);
        };

        var messageHandler = function(event) {
            if (!event.data || event.data.type !== 'instagram_oauth_code') return;
            if (popup && !popup.closed) popup.close();

            getSupabaseToken()
                .then(function(token) {
                    if (!token) throw new Error('no-token');
                    return fetch(SUPABASE_URL + '/functions/v1/instagram-oauth', {
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'apikey': SUPABASE_ANON_KEY,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ code: event.data.code, redirect_uri: redirectUri }),
                    });
                })
                .then(function(res) { return res.json().then(function(b) { return { ok: res.ok, body: b }; }); })
                .then(function(result) {
                    if (!result.ok) throw new Error((result.body && result.body.error) || 'Falha ao conectar');
                    message.success('Instagram conectado com sucesso!');
                    setConnection(result.body && result.body.data);
                })
                .catch(function(err) {
                    message.error(err.message || 'Erro ao conectar o Instagram');
                })
                .finally(finish);
        };
        window.addEventListener('message', messageHandler);

        var poll = setInterval(function() {
            if (popup && popup.closed) finish();
        }, 1000);
    }, []);

    var handleDisconnect = useCallback(function() {
        Modal.confirm({
            title: 'Desconectar Instagram?',
            content: 'A cadência deixará de receber mensagens diretas até uma nova conexão.',
            okText: 'Desconectar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: function() {
                return getSupabaseToken()
                    .then(function(token) {
                        return fetch(SUPABASE_URL + '/rest/v1/instagram_connections?is_active=eq.true', {
                            method: 'PATCH',
                            headers: {
                                'Authorization': 'Bearer ' + token,
                                'apikey': SUPABASE_ANON_KEY,
                                'Content-Type': 'application/json',
                                'Prefer': 'return=minimal',
                            },
                            body: JSON.stringify({ is_active: false }),
                        });
                    })
                    .then(function(res) {
                        if (!res.ok) throw new Error('disconnect-failed');
                        message.success('Instagram desconectado');
                        setConnection(null);
                    })
                    .catch(function() {
                        message.error('Erro ao desconectar');
                    });
            },
        });
    }, []);

    if (loading) {
        return <Button icon="instagram" loading disabled>Instagram</Button>;
    }

    if (connection && connection.username) {
        var goToSettings = function() {
            history.push('/configuracoes/integracoes/instagram');
        };
        var content = (
            <div style={{ width: 296, padding: '2px 0' }}>
                {/* Header: gradient avatar + handle + live status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                    <div style={{
                        width: 44, height: 44, borderRadius: 12,
                        background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 3px 10px rgba(131, 58, 180, 0.25)',
                        flexShrink: 0,
                    }}>
                        <Icon type="instagram" style={{ fontSize: 22, color: '#fff' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                            fontWeight: 600, fontSize: 14, color: '#262626',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            @{connection.username}
                        </div>
                        <div style={{ fontSize: 11, color: '#52c41a', display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                            <span style={{
                                width: 7, height: 7, borderRadius: '50%', background: '#52c41a',
                                boxShadow: '0 0 0 3px rgba(82, 196, 26, 0.18)',
                                animation: 'ig-pulse 2s ease-in-out infinite',
                            }} />
                            Recebendo DMs
                        </div>
                    </div>
                </div>

                {/* Stat cards */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                    <Popover
                        placement="bottom"
                        overlayStyle={{ maxWidth: 320 }}
                        mouseEnterDelay={0.3}
                        title={<span><Icon type="link" style={{ marginRight: 6, color: '#1890ff' }} />Tempo conectado</span>}
                        content={
                            <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.6 }}>
                                <p style={{ margin: 0, marginBottom: 6 }}>
                                    Há quanto tempo a sua conta do Instagram está conectada ao Contlabs.
                                </p>
                                <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                    Conectado em <strong>{connection.subscribed_at ? moment(connection.subscribed_at).format('DD/MM/YYYY HH:mm') : '—'}</strong>.
                                </div>
                                <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 6 }}>
                                    A conexão é renovada automaticamente — você não precisa fazer login de novo.
                                </div>
                            </div>
                        }
                    >
                        <div style={{ flex: 1, padding: '10px 12px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0', cursor: 'help' }}>
                            <div style={{ fontSize: 10, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Icon type="link" style={{ fontSize: 10 }} /> Conectado
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: '#262626' }}>
                                {connection.subscribed_at ? moment(connection.subscribed_at).fromNow(true) : '—'}
                            </div>
                        </div>
                    </Popover>
                    <Popover
                        placement="bottom"
                        overlayStyle={{ maxWidth: 320 }}
                        mouseEnterDelay={0.3}
                        title={
                            <span>
                                <Icon type="message" style={{ marginRight: 6, color: connection.last_event_at ? '#52c41a' : '#bfbfbf' }} />
                                {connection.last_event_at ? 'Última DM recebida' : 'Nenhuma DM ainda'}
                            </span>
                        }
                        content={
                            <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.6 }}>
                                {connection.last_event_at ? (
                                    <>
                                        <p style={{ margin: 0, marginBottom: 6 }}>
                                            Última mensagem que entrou em <strong>{moment(connection.last_event_at).format('DD/MM/YYYY HH:mm:ss')}</strong>.
                                        </p>
                                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                            Atualizado automaticamente toda vez que alguém te manda uma DM.
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <p style={{ margin: 0, marginBottom: 6 }}>
                                            A conexão está ativa, mas ninguém te mandou DM ainda.
                                        </p>
                                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                            Para testar, envie uma DM da sua conta pessoal para <strong>@{connection.username}</strong>.
                                        </div>
                                    </>
                                )}
                            </div>
                        }
                    >
                        <div style={{ flex: 1, padding: '10px 12px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0', cursor: 'help' }}>
                            <div style={{ fontSize: 10, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Icon type="message" style={{ fontSize: 10 }} /> Última DM
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: connection.last_event_at ? '#262626' : '#bfbfbf' }}>
                                {connection.last_event_at ? moment(connection.last_event_at).fromNow(true) : 'Nenhuma'}
                            </div>
                        </div>
                    </Popover>
                </div>

                {/* Primary action */}
                <Button type="primary" icon="setting" block onClick={goToSettings} style={{ marginBottom: 8 }}>
                    Abrir configurações
                </Button>

                {/* Secondary disconnect — subtle, less destructive-looking */}
                <a
                    onClick={handleDisconnect}
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: 6, fontSize: 12, color: '#bfbfbf',
                        padding: '4px 0',
                    }}
                >
                    <Icon type="disconnect" />
                    Desconectar conta
                </a>

                <style>{
                    '@keyframes ig-pulse {' +
                    '  0%, 100% { box-shadow: 0 0 0 3px rgba(82, 196, 26, 0.18); }' +
                    '  50% { box-shadow: 0 0 0 6px rgba(82, 196, 26, 0); }' +
                    '}'
                }</style>
            </div>
        );
        return (
            <Popover
                content={content}
                trigger="click"
                placement="bottomRight"
                overlayStyle={{ paddingTop: 4 }}
            >
                <Button
                    style={{
                        width: 32, height: 32, padding: 0,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: 8,
                        borderColor: '#d9d9d9',
                        position: 'relative',
                    }}
                    aria-label={'Instagram conectado: @' + connection.username}
                >
                    <span style={{
                        width: 20, height: 20, borderRadius: 6,
                        background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Icon type="instagram" style={{ fontSize: 12, color: '#fff' }} />
                    </span>
                    {connection.last_event_at && (
                        <span style={{
                            position: 'absolute', top: 4, right: 4,
                            width: 7, height: 7, borderRadius: '50%',
                            background: '#52c41a',
                            border: '1.5px solid #fff',
                        }} />
                    )}
                </Button>
            </Popover>
        );
    }

    return (
        <Button icon="instagram" onClick={handleConnect} loading={connecting}>
            Conectar Instagram
        </Button>
    );
};

export default InstagramConnect;
